<script setup lang="ts">
/**
 * ImportDialog — EFT format paste & import
 *
 * Props:
 *   visible — controls dialog visibility
 *
 * Events:
 *   close — when dialog should close
 *   imported — when a fit is successfully imported (fitID)
 */
import { ref, watch } from 'vue';
import { importEft, rememberFitEditToken } from '../../composables/useApi';
import { useFitStore } from '../../stores/fitStore';

const props = defineProps<{
  visible: boolean;
}>();

const emit = defineEmits<{
  close: [];
  imported: [fitID: number];
}>();

const store = useFitStore();
const eftText = ref('');
const importing = ref(false);
const error = ref('');

watch(() => props.visible, (v) => {
  if (v) {
    eftText.value = '';
    error.value = '';
    importing.value = false;
  }
});

async function handleImport(): Promise<void> {
  const text = eftText.value.trim();
  if (!text) {
    error.value = 'Paste an EFT format fitting';
    return;
  }

  // Basic validation: first line should be [Ship, Name]
  if (!text.startsWith('[')) {
    error.value = 'Invalid EFT format. First line should be [Ship Name, Fit Name]';
    return;
  }

  importing.value = true;
  error.value = '';

  try {
    const { fitID, editToken } = await importEft(text);
    rememberFitEditToken(fitID, editToken);
    store.addToast('success', 'Fitting imported successfully');
    emit('imported', fitID);
    emit('close');
  } catch (e) {
    error.value = e instanceof Error ? e.message : 'Import failed';
  } finally {
    importing.value = false;
  }
}

function handleKeydown(e: KeyboardEvent): void {
  if (e.key === 'Escape') {
    emit('close');
  }
}
</script>

<template>
  <Teleport to="body">
    <Transition name="modal">
    <div v-if="visible" class="modal-overlay" @click.self="emit('close')" @keydown="handleKeydown">
      <div class="modal-dialog">
        <div class="modal-header">
          <h3 class="modal-title">Import Fitting (EFT)</h3>
          <button class="modal-close" @click="emit('close')">&times;</button>
        </div>

        <div class="modal-body">
          <p class="modal-hint">Paste an EFT format fitting below:</p>
          <textarea
            v-model="eftText"
            class="eft-input"
            placeholder="[Raven, My Fit Name]
Ballistic Control System II
Ballistic Control System II

Adaptive Invulnerability Field II
Large Shield Extender II
..."
            rows="14"
            spellcheck="false"
            autofocus
          />
          <p v-if="error" class="import-error">{{ error }}</p>
        </div>

        <div class="modal-footer">
          <button class="btn btn--secondary" @click="emit('close')">Cancel</button>
          <button class="btn btn--primary" :disabled="importing || !eftText.trim()" @click="handleImport">
            {{ importing ? 'Importing...' : 'Import' }}
          </button>
        </div>
      </div>
    </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.65);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
}

.modal-dialog {
  background: var(--bg-panel);
  border: 1px solid var(--border-color);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-lg);
  width: 480px;
  max-width: 90vw;
  max-height: 85vh;
  display: flex;
  flex-direction: column;
  animation: modal-in 0.15s ease-out;
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  border-bottom: 1px solid var(--border-color);
}

.modal-title {
  font-size: 0.95rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
}

.modal-close {
  background: none;
  border: none;
  color: var(--text-secondary);
  font-size: 1.2rem;
  cursor: pointer;
  padding: 0 4px;
  line-height: 1;
}

.modal-close:hover {
  color: var(--text-primary);
}

.modal-body {
  padding: 16px;
  overflow-y: auto;
}

.modal-hint {
  font-size: 0.82rem;
  color: var(--text-secondary);
  margin: 0 0 8px;
}

.eft-input {
  width: 100%;
  background: var(--bg-darker);
  border: 1px solid var(--border-color);
  border-radius: var(--radius-sm);
  color: var(--text-primary);
  font-family: 'Consolas', 'Monaco', monospace;
  font-size: 0.78rem;
  padding: 10px;
  resize: vertical;
  line-height: 1.5;
}

.eft-input:focus {
  outline: none;
  border-color: var(--accent-blue);
}

.eft-input::placeholder {
  color: var(--text-secondary);
  opacity: 0.5;
}

.import-error {
  font-size: 0.78rem;
  color: var(--accent-red);
  margin: 8px 0 0;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 12px 16px;
  border-top: 1px solid var(--border-color);
}

.btn {
  padding: 6px 16px;
  border: 1px solid var(--border-color);
  border-radius: var(--radius-sm);
  font-size: 0.82rem;
  font-weight: 500;
  cursor: pointer;
  transition: all var(--transition-fast);
}

.btn--secondary {
  background: transparent;
  color: var(--text-secondary);
}

.btn--secondary:hover {
  color: var(--text-primary);
  border-color: var(--text-secondary);
}

.btn--primary {
  background: var(--accent-blue);
  border-color: var(--accent-blue);
  color: #fff;
}

.btn--primary:hover:not(:disabled) {
  background: #3a8eef;
}

.btn--primary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Modal transitions */
.modal-enter-active {
  transition: opacity 0.2s ease;
}

.modal-enter-active .modal-dialog {
  animation: modal-in 0.2s ease-out;
}

.modal-leave-active {
  transition: opacity 0.15s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

@keyframes modal-in {
  from {
    transform: translateY(-8px) scale(0.97);
  }
  to {
    transform: translateY(0) scale(1);
  }
}
</style>
